import { useSelector } from 'react-redux';

import { RootState } from 'store';
import { FormatTypeEnum } from 'modules/I18N';

import { DealSummaryCardSectionLineType } from 'components/pages/DealWithLines/common/RightSidebar/DealSummary/DealSummaryCardSection/DealSummaryCardSection.types';
import { CardTheme } from 'lib/Card/Card.types';

import { handleValueFormat } from 'components/pages/DealWithLines/common/RightSidebar/utils';
import { DealSummaryCard } from 'components/pages/DealWithLines/common/RightSidebar/DealSummary/DealSummaryCard/DealSummaryCard';
import DealSummaryCardSection from 'components/pages/DealWithLines/common/RightSidebar/DealSummary/DealSummaryCardSection';
import Button, { Color, Size } from 'lib/Button';
import listSvg from 'assets/icons/list.svg';
import { LevelType } from 'consts/thoughtspot';

import { DealStatus, DealType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { AllocationFrameList } from 'components/common/Deal/AllocationFrameList/AllocationFrameList';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsProgrammaticCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { AllocationReports } from 'components/common/AllocationReport/AllocationReports/AllocationReports';
import { useToggle } from 'customHooks/useToggle';
import { ReportType } from 'components/common/AllocationReport/AllocationReport.types';
import { AllocationReportButton } from 'components/pages/Planner/PlannerSummaryPanel/AllocationCard/AllocationReportButton/AllocationReportButton';
import { ProposalCardProps } from './ProposalCard.types';

export const ProposalCard = ({
  assets,
  totalCost,
  allocatedImpressions,
  availableImpressions,
  allocatedFrames,
  availableFrames,
  isAvailabilityHidden = false,
  defaultAllocationReportLevelType = LevelType.LINE,
  isVisible,
}: ProposalCardProps): React.ReactElement => {
  const { isOpen: isAllocationReportOpen, open: openReport, close: closeReport } = useToggle(false);

  const { currencyCode, localeCode } = useSelector((state: RootState) => state.publisher.configuration);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const dealType = useSelector(getDealType);
  const temporaryDealId = useSelector((state: Store) => state.dealManagement.temporaryDealId);
  const isProgrammaticCampaignType = useSelector(getIsProgrammaticCampaignType);

  const isGuaranteed = dealType === DealType.GUARANTEED;
  const isReadOnly = isGuaranteed && bookingStatusCode === DealStatus.CANCELLED;

  const buttonsVisibility = {
    frameList: defaultAllocationReportLevelType === LevelType.LINE && !!assets?.length && isProgrammaticCampaignType,
    report: isGuaranteed && bookingStatusCode !== DealStatus.CANCELLED,
  };
  const showButtonsContainer = Object.values(buttonsVisibility).some((isButtonVisible) => isButtonVisible);

  const handleAllocationModalClose = (): void => {
    closeReport();
  };

  return (
    <>
      <DealSummaryCard
        header="PROPOSAL"
        isVisible={isVisible}
        theme={CardTheme.SECONDARY}
        className="pointer-events-auto"
      >
        <DealSummaryCardSection
          lines={[
            {
              label: currencyCode,
              value: handleValueFormat({
                valueType: FormatTypeEnum.NUMBER,
                value: totalCost,
                currencyCode,
                localeCode,
              }),
              lineType: DealSummaryCardSectionLineType.HEADER,
            },
          ]}
          isReadOnly={isReadOnly}
          id="proposal-price"
        />
        <DealSummaryCardSection
          lines={[
            {
              label: 'Impressions',
              value: handleValueFormat({
                valueType: FormatTypeEnum.ROUNDED_NUMBER,
                value: allocatedImpressions,
                currencyCode,
                localeCode,
              }),
              lineType: DealSummaryCardSectionLineType.HEADER,
            },
            ...(isAvailabilityHidden
              ? []
              : [
                  {
                    label: 'Available',
                    value: handleValueFormat({
                      valueType: FormatTypeEnum.ROUNDED_NUMBER,
                      value: availableImpressions,
                      currencyCode,
                      localeCode,
                    }),
                    lineType: DealSummaryCardSectionLineType.SUBHEADER_SECONDARY,
                  },
                ]),
          ]}
          isReadOnly={isReadOnly}
          id="proposal-impressions"
        />
        <DealSummaryCardSection
          lines={[
            {
              label: 'Frames',
              value: handleValueFormat({
                valueType: FormatTypeEnum.NUMBER,
                value: allocatedFrames,
                currencyCode,
                localeCode,
              }),
              lineType: DealSummaryCardSectionLineType.HEADER,
            },
            ...(isAvailabilityHidden
              ? []
              : [
                  {
                    label: 'Available',
                    value: handleValueFormat({
                      valueType: FormatTypeEnum.NUMBER,
                      value: availableFrames,
                      currencyCode,
                      localeCode,
                    }),
                    lineType: DealSummaryCardSectionLineType.SUBHEADER_SECONDARY,
                  },
                ]),
          ]}
          isReadOnly={isReadOnly}
          id="proposal-frames"
        >
          {showButtonsContainer && (
            <div className="flex flex-row gap-x-2 h-9">
              {buttonsVisibility.frameList && (
                <AllocationFrameList
                  assets={assets}
                  actionButton={(onClick) => (
                    <Button
                      color={Color.TERTIARY}
                      label="Frame list"
                      size={Size.WIDE}
                      svg={listSvg}
                      onClick={onClick}
                    />
                  )}
                />
              )}
              {buttonsVisibility.report && <AllocationReportButton openReport={openReport} />}
            </div>
          )}
        </DealSummaryCardSection>
      </DealSummaryCard>
      <AllocationReports
        isAllocationReportOpen={isAllocationReportOpen}
        handleAllocationModalClose={handleAllocationModalClose}
        reportType={temporaryDealId ? ReportType.NOT_SAVED : ReportType.SAVED}
        initialReportType="allocation"
      />
    </>
  );
};
